import { createGrowthEventId, getGrowthEventAnonymousId, getGrowthEventSessionId } from './growthEvents'

const KEY = 'mm_share_attribution'
const TASKS = 'mm_share_generation_tasks'
const WINDOW_MS = 7 * 24 * 60 * 60 * 1000
export interface ShareAttribution { token: string; song_id: string; opened_at: number }
const read = (key: string) => {
  if (typeof window === 'undefined') return null
  try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null }
}
const write = (key: string, value: unknown) => {
  if (typeof window === 'undefined') return
  try { localStorage.setItem(key, JSON.stringify(value)) } catch { /* Private browsing is still usable. */ }
}
const readTasks = (): Record<string, any> => {
  const value = read(TASKS)
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}
export function getShareAttribution(): ShareAttribution | null {
  const value = read(KEY)
  if (typeof value?.token !== 'string' || !value.token || typeof value.song_id !== 'string' || !value.song_id || !Number.isFinite(value.opened_at) || Date.now() < value.opened_at || Date.now() - value.opened_at > WINDOW_MS) return null
  return value
}
export function saveShareAttribution(token: string, song_id: string) {
  if (token && song_id) write(KEY, { token, song_id, opened_at: Date.now() })
}
export function shareAttributionParams() {
  const attribution = getShareAttribution()
  return attribution ? { share_token: attribution.token, anonymous_id: getGrowthEventAnonymousId() } : {}
}
export function shareSignUpOptions<T extends { unsafeMetadata?: Record<string, unknown> }>(options: T) {
  return { ...options, unsafeMetadata: { ...options.unsafeMetadata, ...shareAttributionParams() } }
}
export function shareGrowthEvent(event_type: string, content_id: string, share_token: string, request_id: string, extra: Record<string, unknown> = {}) {
  return { event_id: createGrowthEventId(), request_id, session_id: getGrowthEventSessionId(), anonymous_id: getGrowthEventAnonymousId(), content_type: 'music' as const, surface: 'share_page' as const, content_id, event_type, share_token, ...extra }
}
export function rememberShareGeneration(taskId: string, params: ReturnType<typeof shareAttributionParams>) {
  if (!taskId || !params.share_token) return
  const tasks = readTasks()
  // Retain the attribution at submission, even if another share is opened later.
  tasks[taskId] = { ...params, started_at: Date.now(), request_id: createGrowthEventId() }
  write(TASKS, Object.fromEntries(Object.entries(tasks).filter(([, v]) => v && Date.now() - v.started_at <= WINDOW_MS)))
}
export function completeShareGeneration(task: any) {
  if (typeof window === 'undefined' || task?.status !== 3 || task.action !== 'MUSIC') return null
  const song = task.music_data?.find((item: any) => item.id && (item.audio_url || item.audio_oss || item.stream_audio_url))
  const tasks = readTasks()
  const entry = tasks[task.id]
  if (!song || !entry || typeof entry !== 'object' || !entry.share_token || !entry.anonymous_id || entry.event) return null
  const event = shareGrowthEvent('create_from_share', song.id, entry.share_token, entry.request_id)
  event.anonymous_id = entry.anonymous_id
  entry.event = event
  entry.song = song
  write(TASKS, tasks)
  return event
}
export function consumePostCreateShare(taskId: string, ownerId = 'browser') {
  const tasks = readTasks()
  const entry = tasks[taskId]
  const shownKey = `mm_share_post_create_prompt_shown:${ownerId}`
  if (!entry?.event || read(shownKey)) return null
  write(shownKey, true)
  return entry.song
}
